/** Matches API `LinkedInUrl` field validation (`app/schemas/field.py`). */
export const LINKEDIN_PROFILE_PATH_REGEX =
  /^(https?:\/\/)?([\w]+\.)?linkedin\.com\/(pub|in|profile)\/.+/i;

export const LINKEDIN_URL_MAX_LENGTH = 128;

export const LINKEDIN_URL_INVALID_MESSAGE =
  'Must be in the linkedin.com/in/username format.';

export const formatLinkedInUrl = (value: string) => {
  const needle = 'https://';
  if (value.startsWith(needle)) {
    return value.slice(needle.length);
  }
  return value;
};

/**
 * Value for forms that show a visual `https://` prefix (user enters from linkedin.com).
 */
export function linkedinUrlFormDefault(
  linkedin_url: string | null | undefined,
): string | undefined {
  if (!linkedin_url?.trim()) {
    return undefined;
  }
  const idx = linkedin_url.toLowerCase().indexOf('linkedin.com');
  if (idx === -1) {
    return linkedin_url.trim();
  }
  return linkedin_url.slice(idx);
}

/** Same as {@link linkedinUrlFormDefault} but uses empty string when unset. */
export function linkedinUrlFormDefaultOrEmpty(
  linkedin_url: string | null | undefined,
): string {
  return linkedinUrlFormDefault(linkedin_url) ?? '';
}

export function isValidLinkedInProfileUrl(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) {
    return true;
  }

  let normalized = trimmed.toLowerCase();
  if (!normalized.startsWith('http')) {
    normalized = `https://${normalized}`;
  }

  return LINKEDIN_PROFILE_PATH_REGEX.test(normalized);
}

/**
 * Normalizes a form value for API submission (lowercase, ensures https://).
 * Returns null when cleared; undefined when input is nullish.
 */
export function normalizeLinkedInUrlForApi(
  value: string | null | undefined,
): string | null | undefined {
  if (value == null) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  let normalized = trimmed.toLowerCase();
  if (!normalized.startsWith('http')) {
    normalized = `https://${normalized}`;
  }

  if (!LINKEDIN_PROFILE_PATH_REGEX.test(normalized)) {
    return null;
  }

  return normalized;
}

/** Builds a clickable URL from a form field value (may omit protocol). */
export function linkedinUrlHrefFromFormValue(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export function getSafeAppRedirectPath(
  value: string | null | undefined,
): string | undefined {
  const path = value?.trim();
  if (
    !path ||
    path.length <= 1 ||
    !path.startsWith('/') ||
    path.startsWith('//') ||
    path.startsWith('/\\')
  ) {
    return undefined;
  }

  return path;
}

/**
 * Route params for the trending-recommendation deeplink carried by an
 * external "request an intro" (preregistration) join link, or undefined
 * when the link has no deeplink or the user has no team to view it on.
 * Mirrors where the post-signup flow lands preregistered users (BAN-5923).
 */
export function getJoinIntroDeeplinkParams(
  searchStr: string | null | undefined,
  teamSlug: string | null | undefined,
): { recommendationId: string; teamId: string } | undefined {
  if (!teamSlug) {
    return undefined;
  }
  const recommendationId = new URLSearchParams(searchStr ?? '').get(
    'recommendation_uuid',
  );
  if (!recommendationId) {
    return undefined;
  }
  return { recommendationId, teamId: teamSlug };
}
